import { useState, useEffect } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Switch, FormControlLabel, Avatar, Stack, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Grid from '@mui/material/GridLegacy';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, setHours, setMinutes, isSameDay, addDays } from 'date-fns';
import { sportEventApi, matchApi as cricketMatchApi } from '../../../services/api';
import {matchApi as footballMatchApi} from '../../../services/footballApi';
import { useSnackbar } from 'notistack';
import type { SportEvent, Match } from '../../../models';
import EventDetailsDrawer from './components/EventDetailsDrawer';
import {
  CRICKET_XTRACT_PLATFORM_ICONS,
  CRICKET_XTRACT_PLATFORMS,
  CRICKET_SPORT_EVENT_FETCH_PLATFORMS,
} from '../cricketPlatforms';

interface TimeSlot {
  hour: number;
  minute: number;
}

interface Platform {
id: string;
  name: string;
}

interface EventsState {
  d11: SportEvent[];
  c11: SportEvent[];
  mt11: SportEvent[];
  f11: SportEvent[];
  v11: SportEvent[];
  cb11: SportEvent[];
  cricbuzz: SportEvent[];
  crex: SportEvent[];
  espn: SportEvent[];
  fotmob: SportEvent[];
  ai11: Match[];
}

const EMPTY_EVENTS: EventsState = {
  d11: [],
  c11: [],
  mt11: [],
  f11: [],
  v11: [],
  cb11: [],
  cricbuzz: [],
  crex: [],
  espn: [],
  fotmob: [],
  ai11: [],
};

// Generate time slots for every 30 minutes
const TIME_SLOTS: TimeSlot[] = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = (i % 2) * 30;
  return { hour, minute };
});

// Platform icon mapping (cricket platforms shared; football/basketball extras below)
const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  ...CRICKET_XTRACT_PLATFORM_ICONS,
  fotmob: <Avatar sx={{ width: 24, height: 24, bgcolor: '#f57c00', fontSize: 14 }}>FM</Avatar>,
};

const CRICKET_PLATFORMS = [
  ...CRICKET_XTRACT_PLATFORMS.filter((p) => p.id !== 'espn'),
  { id: 'espn', name: 'ESPN', visible: true },
  { id: 'ai11', name: 'AI11', visible: true },
];

const FOOTBALL_PLATFORMS = [
  { id: 'd11', name: 'Dream11', visible: true },
  { id: 'fotmob', name: 'FotMob', visible: true },
  { id: 'c11', name: 'My11Circle', visible: false },
  { id: 'mt11', name: 'MyTeam11', visible: false },
  { id: 'f11', name: 'Fab11', visible: false },
  { id: 'v11', name: 'Vision11', visible: true },
  { id: 'ai11', name: 'AI11', visible: true }
];

const BASKETBALL_PLATFORMS = [
  { id: 'v11', name: 'Vision11', visible: true }
];

type SportType = 'cricket' | 'football' | 'kabaddi' | 'basketball';
const getPlatforms = (sport: SportType) => {
  if (sport === 'cricket') return CRICKET_PLATFORMS;
  if (sport === 'football') return FOOTBALL_PLATFORMS;
  if (sport === 'basketball') return BASKETBALL_PLATFORMS;
  return [];
};

const SportEvents: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSport, setSelectedSport] = useState<SportType>('cricket');
  const [events, setEvents] = useState<EventsState>(EMPTY_EVENTS);
  // const [loading, setLoading] = useState<boolean>(true);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [selectedEvent, setSelectedEvent] = useState<SportEvent | Match | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [visiblePlatforms, setVisiblePlatforms] = useState<{ [key: string]: boolean }>({});
  const { enqueueSnackbar } = useSnackbar();

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchEvents = async (): Promise<void> => {
    try {
      setRefreshing(true);
      // setLoading(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const nextDateStr = format(addDays(selectedDate, 1), 'yyyy-MM-dd');
      const start_time_after = `${dateStr}T00:00:00+05:30`;
      const start_time_before = `${nextDateStr}T00:00:00+05:30`;
      
      // For basketball, only fetch v11 events
      if (selectedSport === 'basketball') {
        const v11Res = await sportEventApi.getAll({
          platform: 'v11',
          sport: selectedSport,
          start_time_after,
          start_time_before,
          page_size: 100
        });

        setEvents({
          ...EMPTY_EVENTS,
          v11: v11Res.data.results || [],
        });
      } else {
        const fetchPlatforms =
          selectedSport === 'cricket'
            ? CRICKET_SPORT_EVENT_FETCH_PLATFORMS
            : (['d11', 'c11', 'mt11', 'f11', 'v11'] as const);

        const platformFetches = fetchPlatforms.map(async (platform) => {
          const res = await sportEventApi.getAll({
            platform,
            sport: selectedSport,
            start_time_after,
            start_time_before,
            ...(platform === 'f11' ? { is_second_inning: false } : {}),
            page_size: 100,
          });
          return [platform, res.data.results || []] as const;
        });

        const [platformResults, baseRes, ai11Res] = await Promise.all([
          Promise.all(platformFetches),
          sportEventApi.getAll({
            platform: selectedSport === 'football' ? 'fotmob' : 'espn',
            sport: selectedSport,
            start_time_after,
            start_time_before,
            page_size: 300,
          }),
          (selectedSport === 'football' ? footballMatchApi : cricketMatchApi).getAll({
            start_time_after,
            start_time_before,
            page_size: 100,
          }),
        ]);

        const nextEvents: EventsState = { ...EMPTY_EVENTS };
        platformResults.forEach(([platform, results]) => {
          nextEvents[platform] = results;
        });
        nextEvents.espn = selectedSport === 'cricket' ? baseRes.data.results || [] : [];
        nextEvents.fotmob = selectedSport === 'football' ? baseRes.data.results || [] : [];
        nextEvents.ai11 = ai11Res.data.results || [];
        setEvents(nextEvents);
      }

      // Initialize visible state for all platforms
      const initialVisibleState = getPlatforms(selectedSport).reduce((acc, platform) => {
        acc[platform.id] = platform.visible; // All platforms visible by default
        return acc;
      }, {} as { [key: string]: boolean });
      setVisiblePlatforms(initialVisibleState);
    } catch (error) {
      console.error('Error fetching events:', error);
      enqueueSnackbar('Error fetching events', { variant: 'error' });
    } finally {
      setRefreshing(false);
      // setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedSport]);

  const togglePlatform = (platformId: string) => {
    setVisiblePlatforms((prev) => ({
      ...prev,
      [platformId]: !prev[platformId],
    }));
  };

  const toggleAllPlatforms = () => {
    const allVisible = Object.values(visiblePlatforms).every((v) => v);
    const newState = getPlatforms(selectedSport).reduce((acc, platform) => {
      acc[platform.id] = !allVisible;
      return acc;
    }, {} as { [key: string]: boolean });
    setVisiblePlatforms(newState);
  };

  const getEventsForTimeSlot = (hour: number, minute: number, platform: Platform['id']): (SportEvent | Match)[] => {
    if (platform === 'ai11') {
      const platformEvents = events[platform] || [];
      return platformEvents.filter((event: Match) => {
        const eventTime = new Date(event.start_date);
        return eventTime.getHours() === hour && eventTime.getMinutes() >= minute && eventTime.getMinutes() < minute + 30;
      });
    } else {
      const platformEvents = events[platform] || [];
      return platformEvents.filter((event: SportEvent) => {
        const eventTime = new Date(event.start_time);
        const eventHour = eventTime.getHours();
        const eventMinute = eventTime.getMinutes();
        
        // Check if event falls within the current 30-minute slot
        if (eventHour === hour) {
          if (minute === 0) {
            return eventMinute < 30;
          } else {
            return eventMinute >= 30;
          }
        }
        return false;
      });
    }
  };

  const isCurrentTimeSlot = (hour: number, minute: number): boolean => {
    if (!isSameDay(currentTime, selectedDate)) return false;
    return currentTime.getHours() === hour && 
           currentTime.getMinutes() >= minute && 
           currentTime.getMinutes() < minute + 30;
  };

  const handleEventClick = (event: SportEvent | Match, platform: Platform['id']) => {
    setSelectedEvent(event);
    setSelectedPlatform(platform);
  };

  const handleCloseDrawer = () => {
    setSelectedEvent(null);
    setSelectedPlatform(null);
  };

  const handleRefresh = () => {
    fetchEvents();
  };

  const renderEventContent = (event: SportEvent | Match, platform: Platform['id']) => {
    if (platform === 'ai11') {
      const matchEvent = event as Match;
      return (
        <>
        <Grid container>
          <Grid item xs={8}>
          <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
            {matchEvent.name || 'TBD vs TBD'}
          </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="textDisabled" sx={{ fontSize: '0.6rem' }}>
            {matchEvent.id}
          </Typography>
          </Grid>
        </Grid>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem' }}>
            {matchEvent.series?.name || ''}
          </Typography>
        </>
      );
    }
    const sportEvent = event as SportEvent;
    return (
      <>
      <Grid container>
        <Grid item xs={8}>
        <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
          {sportEvent.team1_short_name ?? sportEvent.team1_name} vs {sportEvent.team2_short_name ?? sportEvent.team2_name}
        </Typography>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'right' }}>
        <Typography variant="caption" color="textDisabled" sx={{ fontSize: '0.6rem' }}>
            {sportEvent.platform_id}
          </Typography>
        </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem' }}>
          {sportEvent.series_name}
        </Typography>
      </>
    );
  };

  const renderTimeSlot = (timeSlot: TimeSlot) => {
    const { hour, minute } = timeSlot;
    const isCurrentSlot = isCurrentTimeSlot(hour, minute);
    const timeStr = format(setMinutes(setHours(new Date(), hour), minute), 'hh:mm a');

    return (
      <Grid item xs={12} key={`${hour}-${minute}`} sx={{ 
        display: 'flex', 
        borderBottom: '1px solid #eee',
        position: 'relative'
      }}>
        {isCurrentSlot && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: '2px',
              backgroundColor: 'error.main',
              zIndex: 1
            }}
          />
        )}
        <Box sx={{ width: 70, p: 0.5, borderRight: '1px solid #eee' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {timeStr}
          </Typography>
        </Box>
        {getPlatforms(selectedSport)
          .filter(platform => visiblePlatforms[platform.id])
          .map(platform => {
          const slotEvents = getEventsForTimeSlot(hour, minute, platform.id);
          return (
            <Box
              key={platform.id}
              sx={{
                flex: 1,
                p: 0.5,
                borderRight: '1px solid #eee',
                bgcolor: slotEvents.length > 0 ? 'action.hover' : 'transparent'
              }}
            >
              {slotEvents.map((event, index) => {
                const hasMatchId = platform.id === 'ai11' ? true : (event as SportEvent).match_id;
                const isCancelled = platform.id !== 'ai11' && (event as any).status === 'cancelled';
                return (
                  <Box
                    key={event.id}
                    sx={{
                      cursor: 'pointer',
                      mb: index < slotEvents.length - 1 ? 0.5 : 0,
                      p: 0.5,
                      borderRadius: 0.5,
                      bgcolor: isCancelled
                        ? 'transparent'
                        : hasMatchId
                          ? 'background.paper'
                          : 'error.lighter',
                      border: isCancelled
                        ? 'none'
                        : hasMatchId
                          ? 'none'
                          : '1px solid',
                      borderColor: isCancelled
                        ? 'none'
                        : hasMatchId
                          ? 'none'
                          : 'error.main',
                      '&:hover': {
                        bgcolor: isCancelled
                          ? 'transparent'
                          : hasMatchId
                            ? 'action.selected'
                            : 'error.light'
                      }
                    }}
                    onClick={() => handleEventClick(event, platform.id)}
                  >
                    <span style={isCancelled ? { textDecoration: 'line-through', color: '#d32f2f' } : {}}>
                      {renderEventContent(event, platform.id)}
                    </span>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Grid>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2 }}>
        <Paper 
          sx={{ 
            p: 1.5, 
            mb: 2,
            position: 'sticky',
            top: 60,
            zIndex: 2,
            backgroundColor: 'background.paper'
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" component="h1" gutterBottom>
                  Sport Events
                </Typography>
                <Tooltip title="Refresh events">
                  <IconButton 
                    onClick={handleRefresh}
                    disabled={refreshing}
                    size="small"
                    sx={{ 
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newValue: Date | null) => newValue && setSelectedDate(newValue)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sport</InputLabel>
                <Select
                  value={selectedSport}
                  label="Sport"
                  onChange={(e) => setSelectedSport(e.target.value as typeof selectedSport)}
                >
                  <MenuItem value="cricket">Cricket</MenuItem>
                  <MenuItem value="football">Football</MenuItem>
                  <MenuItem value="basketball">Basketball</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={toggleAllPlatforms}
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  {Object.values(visiblePlatforms).every((v) => v)
                    ? "Hide All"
                    : "Show All"}
                </Button>
                {getPlatforms(selectedSport).map((platform) => (
                  <Tooltip key={platform.id} title={platform.name} arrow>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={visiblePlatforms[platform.id]}
                          onChange={() => togglePlatform(platform.id)}
                          size="small"
                          color="primary"
                          inputProps={{ 'aria-label': `${platform.name} toggle` }}
                        />
                      }
                      label={PLATFORM_ICONS[platform.id]}
                      labelPlacement="top"
                      sx={{ m: 0, minWidth: 36 }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 1 }}>
          <Grid container>
            <Grid 
              item 
              xs={12} 
              sx={{ 
                display: 'flex', 
                borderBottom: '1px solid #eee',
                position: 'sticky',
                top: 120,
                zIndex: 20,
                backgroundColor: 'background.paper'
              }}
            >
              <Box sx={{ width: 70, p: 0.5, borderRight: '1px solid #eee' }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Time</Typography>
              </Box>
              {getPlatforms(selectedSport)
                .filter(platform => visiblePlatforms[platform.id])
                .map(platform => (
                <Box
                  key={platform.id}
                  sx={{
                    flex: 1,
                    p: 0.5,
                    borderRight: '1px solid #eee',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{platform.name}</Typography>
                </Box>
              ))}
            </Grid>
            {TIME_SLOTS.map(timeSlot => renderTimeSlot(timeSlot))}
          </Grid>
        </Paper>

        <EventDetailsDrawer
          open={!!selectedEvent}
          onClose={handleCloseDrawer}
          event={selectedEvent}
          platform={selectedPlatform && typeof selectedPlatform === 'string' ? (selectedPlatform as any) : 'd11'}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default SportEvents; 